import type {EagColumn} from '../../../../../types/EagColumn';
import type {EagColumnOrGroup} from '../../../../../types/EagColumnGroup';
import {isEagColumnGroup} from '../../../../../utils/isEagColumnGroup';
import type {ColumnGroupMeta} from './buildColumnGroupMeta';

const UNGROUPED_KEY = '__ungrouped__';

export type HiddenColumnBucket = {
  groupId: string;
  /** Sub-header label, or null for the trailing ungrouped bucket. */
  label: string | null;
  columns: EagColumn[];
};

/**
 * Buckets hidden columns by their column group, preserving group declaration
 * order from columnConfig, with ungrouped columns last (label: null, no
 * sub-header). Empty buckets are omitted.
 */
export const groupHiddenColumns = (
  hiddenColumns: EagColumn[],
  columnConfig: EagColumnOrGroup[] | undefined,
  groupMeta: Map<string, ColumnGroupMeta>
): HiddenColumnBucket[] => {
  const groupOrder: string[] = [];
  const seenGroups = new Set<string>();
  for (const item of columnConfig ?? []) {
    if (isEagColumnGroup(item) && !seenGroups.has(item.groupId)) {
      seenGroups.add(item.groupId);
      groupOrder.push(item.groupId);
    }
  }

  const hiddenByGroup = new Map<string, EagColumn[]>();
  for (const col of hiddenColumns) {
    const groupId = groupMeta.get(col.key)?.groupId ?? UNGROUPED_KEY;
    const bucket = hiddenByGroup.get(groupId);
    if (bucket) {
      bucket.push(col);
    } else {
      hiddenByGroup.set(groupId, [col]);
    }
  }

  return [...groupOrder, UNGROUPED_KEY]
    .filter((groupId) => hiddenByGroup.has(groupId))
    .map((groupId) => {
      const columns = hiddenByGroup.get(groupId) ?? [];
      const label =
        groupId === UNGROUPED_KEY ? null : (groupMeta.get(columns[0].key)?.label ?? null);
      return {groupId, label, columns};
    });
};
